/**
 * [C34] The watchdog window (old "نظام الحراسة المستقل وسجل العمليات والأخطاء", WatchdogLogsModal.tsx; D-136): the
 * server watchdog's status for holders of `view_logs_watchdog` — from the dashboard's header button and, as before,
 * Ctrl+Shift+L anywhere (old App.tsx:424-438). Live refresh every 15 s while open (pause / resume as the old window).
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { WatchdogWindow, type WatchdogCheckRow, type WatchdogEntry, type WatchdogSeverity, type WatchdogTab, type WatchdogTile } from "@faylasuf/ui";
import type { WatchdogStatusView } from "../api/types.js";
import { useApp, useSession } from "./context.js";
import { fmt } from "../pages/customers/format.js";

const NUMBER_PARAMS = new Set(["projected", "replayed", "balance", "vaultOwn", "custodies", "difference", "baseIqdSum", "iqdEquivalent"]);

export function useWatchdogWindow(): { open: () => void; node: React.ReactNode; allowed: boolean } {
  const { source, t, locale } = useApp();
  const { session, hasPermission } = useSession();
  const allowed = hasPermission("view_logs_watchdog");
  const [isOpen, setOpen] = useState(false);
  const [tab, setTab] = useState<WatchdogTab>("stream");
  const [search, setSearch] = useState("");
  const [level, setLevel] = useState<"ALL" | WatchdogSeverity>("ALL");
  const [module, setModule] = useState("ALL");
  const [live, setLive] = useState(true);
  const [showCleared, setShowCleared] = useState(false);
  const qc = useQueryClient();

  // Ctrl+Shift+L everywhere (old App.tsx:424-438), only for the permission holders (D-136; old: everyone, R-A9)
  useEffect(() => {
    if (!allowed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === "L" || e.key === "l")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [allowed]);

  const q = useQuery({
    queryKey: ["watchdog-status", showCleared],
    queryFn: () => source.watchdogStatus(showCleared ? "all" : "open"),
    enabled: isOpen && allowed,
    refetchInterval: isOpen && live ? 15_000 : false,
  });
  const v: WatchdogStatusView | undefined = q.data;
  const tz = session.office.timeZone;
  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(locale === "ar" ? "ar-IQ" : "en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : t("wd.never"));

  const tiles: WatchdogTile[] = useMemo(() => {
    const check = (name: string) => v?.checks.find((c) => c.name === name);
    const tone = (o: string | null | undefined): WatchdogTile["tone"] => (o === "clean" ? "ok" : o === "findings" ? "warn" : o === "error" ? "bad" : "muted");
    return [
      { labelKey: "wd.tile_integrity", value: when(check("integrity")?.lastRunAt ?? null), tone: tone(check("integrity")?.outcome) },
      { labelKey: "wd.tile_balances", value: when(check("balances")?.lastRunAt ?? null), tone: tone(check("balances")?.outcome) },
      { labelKey: "wd.tile_pending", value: when(check("pending")?.lastRunAt ?? null), tone: tone(check("pending")?.outcome) },
      { labelKey: "wd.tile_matcher", value: when(check("difference_matcher")?.lastRunAt ?? null), tone: check("difference_matcher")?.outcome === "error" ? "bad" : check("difference_matcher")?.lastRunAt ? "ok" : "muted" },
      { labelKey: "wd.tile_critical", value: String(v?.counts.critical ?? 0), tone: (v?.counts.critical ?? 0) > 0 ? "bad" : "ok" },
      { labelKey: "wd.tile_warning", value: String(v?.counts.warning ?? 0), tone: (v?.counts.warning ?? 0) > 0 ? "warn" : "ok" },
    ];
  }, [v, locale]);

  const checks: WatchdogCheckRow[] = (v?.checks ?? []).map((c) => ({ name: c.name, labelKey: `wd.check_${c.name}`, lastRun: c.lastRunAt ? when(c.lastRunAt) : null, outcome: c.outcome, findings: c.findings, error: c.error }));
  const entries: WatchdogEntry[] = (v?.findings ?? []).map((f) => {
    const params: Record<string, string> = {};
    for (const [k, val] of Object.entries(f.detail)) {
      if (typeof val === "string" || typeof val === "number") params[k] = NUMBER_PARAMS.has(k) && typeof val === "string" ? fmt(val) : String(val);
    }
    if (typeof f.detail["type"] === "string") params["type"] = t(`jr.type.${f.detail["type"] as string}`);
    if (!params["name"]) params["name"] = t("common.dash");
    return {
      id: f.id,
      severity: f.severity,
      check: f.check,
      checkLabelKey: `wd.check_${f.check}`,
      time: when(f.lastSeenAt),
      message: t(`wd.finding.${f.kind}`, params),
      details: JSON.stringify({ kind: f.kind, firstSeenAt: f.firstSeenAt, lastSeenAt: f.lastSeenAt, clearedAt: f.clearedAt, ...f.detail }, null, 2),
      cleared: f.clearedAt !== null,
    };
  });

  const node = allowed ? (
    <WatchdogWindow
      open={isOpen}
      onClose={() => setOpen(false)}
      status={v?.status ?? "unknown"}
      tiles={tiles}
      tab={tab}
      onTab={setTab}
      entries={entries}
      checks={checks}
      search={search}
      onSearch={setSearch}
      level={level}
      onLevel={setLevel}
      module={module}
      onModule={setModule}
      live={live}
      onToggleLive={() => setLive((x) => !x)}
      refreshing={q.isFetching}
      onRefresh={() => void qc.invalidateQueries({ queryKey: ["watchdog-status"] })}
      showCleared={showCleared}
      onToggleCleared={() => setShowCleared((x) => !x)}
    />
  ) : null;
  return { open: () => allowed && setOpen(true), node, allowed };
}
